import React, { createContext, useEffect, useState, useContext } from "react";
import { AuthContext } from "./AuthContextWrapper";
import { estateFormsApi, logger } from "./apis";
import { AtticusForm } from "models/AtticusForm";
interface Props {
  children: JSX.Element;
}

export interface EstateContextInterface {
  estateForms?: AtticusForm[];
  estateFormsIsLoading: boolean;
  fetchEstateForms: (estateId: number) => void;
  addEstateForm: (estateId: number, form: AtticusForm) => void;
  removeEstateForm: (estateId: number, form: AtticusForm) => void;
}

export const EstateContext = createContext<EstateContextInterface>({
  estateForms: undefined,
  estateFormsIsLoading: false,
  fetchEstateForms: () => undefined,
  addEstateForm: () => undefined,
  removeEstateForm: () => undefined,
});

export const EstateContextWrapper = ({ children }: Props): JSX.Element => {
  const { activeEstate } = useContext(AuthContext);
  const [estateForms, setEstateForms] = useState<AtticusForm[] | undefined>(
    undefined
  );
  const [estateFormsIsLoading, setEstateFormsIsLoading] =
    useState<boolean>(true);

  // Refetch Forms when Probate Type or Active Estate changes
  useEffect(() => {
    (async () => {
      if (activeEstate?.id) {
        setEstateFormsIsLoading(true);
        try {
          await Promise.all([fetchEstateForms(activeEstate.id)]);
        } catch (e) {
          logger.error(
            "EstateContextWrapper failed to fetch estate forms for estate: " +
              activeEstate.id +
              ", err=" +
              e
          );
        } finally {
          setEstateFormsIsLoading(false);
        }
      }
    })();
  }, [activeEstate]);

  const fetchEstateForms = async (estateId: number): Promise<void> => {
    const fetchedEstateForms = await estateFormsApi.getEstateForms(estateId);
    setEstateForms(fetchedEstateForms);
  };

  const addEstateForm = async (
    estateId: number,
    form: AtticusForm
  ): Promise<void> => {
    const retForm = await estateFormsApi.addFormToEstate(estateId, form);
    setEstateForms([...(estateForms || []), retForm]);
  };

  const removeEstateForm = async (
    estateId: number,
    form: AtticusForm
  ): Promise<void> => {
    if (await estateFormsApi.removeFormFromEstate(estateId, form)) {
      setEstateForms(
        (estateForms || []).filter((estateForm) => estateForm.id !== form.id)
      );
    }
  };

  return (
    <EstateContext.Provider
      value={{
        estateForms,
        fetchEstateForms,
        addEstateForm,
        removeEstateForm,
        estateFormsIsLoading,
      }}
    >
      {children}
    </EstateContext.Provider>
  );
};
